import { createContext, useContext, useState, useCallback } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { useAuth } from "./auth-context";
import { db } from "@/firebase/firebaseConfig";

const VideosContext = createContext({
  videos: [],
  setVideos: () => {},
  fetchVideos: async () => {},
  isLoading: false,
});

export const VideosProvider = ({ children }) => {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const fetchVideos = useCallback(
    async (
      setData,
      courseType,
      setMaxModules,
      setIsLessonCompleted,
      maxModules
    ) => {
      if (!user) return;

      setIsLoading(true);

      try {
        const userRef = doc(db, "users", user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setMaxModules(userData.maxModules || 0);
          setIsLessonCompleted(userData.completedLessons || false);
        }

        const collectionRef = collection(db, courseType);
        const querySnapshot = await getDocs(collectionRef);

        const modulesData = await Promise.all(
          querySnapshot.docs.map(async (doc) => {
            const moduleData = { id: doc.id, ...doc.data() };

            if (Array.isArray(moduleData.videos)) {
              const videoDataPromises = moduleData.videos.map(
                async (videoRef) => {
                  const videoDoc = await getDoc(videoRef);
                  return videoDoc.exists()
                    ? { id: videoDoc.id, ...videoDoc.data() }
                    : null;
                }
              );

              moduleData.videos = (await Promise.all(videoDataPromises)).filter(
                Boolean
              );
            }

            return moduleData;
          })
        );

        const visibleModules = modulesData.filter((module) => module.isVisible);
        const modulesToDisplay =
          maxModules === 0
            ? visibleModules
            : visibleModules.slice(0, maxModules);

        setData(modulesToDisplay);

        const updatedVideosByModule = {};
        modulesToDisplay.forEach((module) => {
          updatedVideosByModule[module.id] = {
            ...module,
            videos: module.videos,
          };
        });

        setVideos(updatedVideosByModule);
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [user]
  );

  return (
    <VideosContext.Provider
      value={{ videos, setVideos, fetchVideos, isLoading }}
    >
      {children}
    </VideosContext.Provider>
  );
};

export const useVideos = () => useContext(VideosContext);
